<template>
  <section class="journey-section" id="journey">
    <div class="container">
      <div class="row justify-content-center text-center mb-4">
        <div class="col-lg-8">
          <h2 class="section-title">One engine. Every step feeds the next.</h2>
          <p class="section-sub">
            Follow one customer around the loop. Nothing here is a separate tool;
            each step hands the same customer record to the next, automatically.
          </p>
        </div>
      </div>

      <!-- Desktop: the loop as instrument on the left, the story on the right -->
      <div
        class="journey-duo"
        @mouseenter="pauseAuto"
        @mouseleave="resumeAuto"
      >
      <div class="journey-ring-wrap">
        <!-- Starfield: fixed pseudo-random constellation dots -->
        <span
          v-for="(p, i) in particles"
          :key="'particle-' + i"
          :class="['journey-particle', p.hue]"
          :style="p.style"
          aria-hidden="true"
        ></span>

        <svg class="journey-ring-svg" viewBox="0 0 100 100" aria-hidden="true">
          <!-- Hairline spokes from the core out to each stage -->
          <line
            v-for="(sp, i) in spokes"
            :key="'spoke-' + i"
            :x1="sp.x1" :y1="sp.y1" :x2="sp.x2" :y2="sp.y2"
            stroke="rgba(255, 255, 255, 0.07)"
            stroke-width="0.3"
          />
          <!-- Track -->
          <circle
            class="journey-ring-circle"
            cx="50" cy="50" r="40"
            fill="none"
            stroke="rgba(255, 255, 255, 0.08)"
            stroke-width="0.4"
          />
          <!-- Comet tail: two layered arcs (faint long + bright short), both
               ending at the active node. Real SVG geometry, so they sit
               exactly on the track at every size and DPR. -->
          <circle
            class="journey-arc"
            :style="{ transform: 'rotate(' + arcRotate(19) + 'deg)' }"
            cx="50" cy="50" r="40"
            fill="none"
            stroke="rgba(225, 78, 202, 0.32)"
            stroke-width="1.4"
            stroke-linecap="round"
            pathLength="100"
            stroke-dasharray="19 81"
          />
          <circle
            class="journey-arc"
            :style="{ transform: 'rotate(' + arcRotate(9) + 'deg)' }"
            cx="50" cy="50" r="40"
            fill="none"
            stroke="rgba(225, 78, 202, 0.9)"
            stroke-width="1"
            stroke-linecap="round"
            pathLength="100"
            stroke-dasharray="9 91"
          />
        </svg>

        <!-- The customer record: glowing dot at the head of the tail -->
        <div
          class="journey-orbiter"
          :style="{ transform: 'rotate(' + rotAngle + 'deg)' }"
          aria-hidden="true"
        >
          <span class="orbit-dot"></span>
        </div>

        <button
          v-for="(step, i) in steps"
          :key="step.label"
          :class="['journey-ring-node', { active: activeIndex === i }]"
          :style="nodePos(i)"
          :aria-current="activeIndex === i ? 'step' : null"
          @click="select(i)"
          @mouseenter="select(i, true)"
          @focus="select(i, true)"
        >
          <span :class="['node-orb', i % 2 ? 'hue-teal' : 'hue-pink']">
            <span class="node-spark"></span>
          </span>
          <span class="node-text">
            <span class="node-label">{{ step.label }}</span>
            <span class="node-caption">{{ step.caption }}</span>
          </span>
        </button>

        <div class="journey-ring-core" aria-hidden="true">
          <span class="core-orb">
            <img src="/img/otc-logo-white.png" alt="" width="52" height="39" />
          </span>
          <span class="core-label">One customer record</span>
        </div>
      </div>

      <div class="journey-detail-card" aria-live="polite">
        <transition name="journey-fade" mode="out-in">
          <div :key="activeIndex">
            <span class="detail-count">{{ activeStep.label }}</span>
            <h3>{{ activeStep.title }}</h3>
            <p>{{ activeStep.body }}</p>
            <div class="journey-chips">
              <span v-for="chip in activeStep.chips" :key="chip">{{ chip }}</span>
            </div>
          </div>
        </transition>
        <div class="journey-detail-nav">
          <button
            aria-label="Previous step"
            @click="select((activeIndex + steps.length - 1) % steps.length)"
          >
            <svg-icon name="arrow-right" :size="16" class="nav-prev" />
          </button>
          <button
            aria-label="Next step"
            @click="select((activeIndex + 1) % steps.length)"
          >
            <svg-icon name="arrow-right" :size="16" />
          </button>
        </div>
      </div>
      </div>

      <!-- Mobile: vertical thread -->
      <div class="journey-mobile">
        <div class="journey">
          <div v-for="step in steps" :key="step.label" class="journey-step">
            <div class="journey-node">
              <svg-icon :name="step.icon" :size="20" />
            </div>
            <div class="journey-card">
              <h3>{{ step.title }}</h3>
              <p>{{ step.body }}</p>
              <div class="journey-chips">
                <span v-for="chip in step.chips" :key="chip">{{ chip }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <p class="journey-loop">
        Then they book again, and the engine already knows them.
      </p>
    </div>
  </section>
</template>

<script>
export default {
  name: 'JourneyFlow',
  data() {
    return {
      activeIndex: 0,
      rotAngle: 0,
      autoTimer: null,
      interacted: false,
      steps: [
        {
          icon: 'calendar',
          label: 'Book',
          caption: 'bookings + payments',
          title: 'They book',
          body: 'Your booking site takes the reservation and payment in one step. The customer record is born the moment money moves.',
          chips: ['Bookings', 'Payments'],
        },
        {
          icon: 'check-circle',
          label: 'Arrive',
          caption: 'waivers + check-in',
          title: 'They sign and walk in',
          body: 'The confirmation email carries a pre-filled waiver link, and signatures sync to the booking on their own. At the door, guests check in by QR code or kiosk; staff and rooms were assigned the moment the booking landed.',
          chips: ['Waivers', 'Check-in', 'Team'],
        },
        {
          icon: 'gamepad',
          label: 'Play',
          caption: 'event tracking',
          title: 'They play',
          body: 'The host runs the session from one screen: timer, hints, music, tracking. Photos go straight to the cloud, branded.',
          chips: ['Event tracking', 'Photos'],
        },
        {
          icon: 'sparkles',
          label: 'Relive',
          caption: 'photos + surveys',
          title: 'They relive it',
          body: 'Branded photos and event stats land in every guest\'s inbox minutes after the finale, with a survey following close behind.',
          chips: ['Photos', 'Surveys', 'Emails'],
        },
        {
          icon: 'bar-chart',
          label: 'Learn',
          caption: 'analytics',
          title: 'You reset and learn',
          body: 'The reset checklist fires for your crew while analytics absorb the session. Every answer and every photo attaches to the customer\'s record.',
          chips: ['Checklists', 'Analytics', 'Customers'],
        },
        {
          icon: 'trending',
          label: 'Return',
          caption: 'marketing + rewards',
          title: 'They come back',
          body: 'Marketing campaigns, gift cards, achievements, leaderboards, and rewards all pull them back in. Next visit, the waiver, preferences, and event history are already on file.',
          chips: ['Marketing', 'Gift cards', 'Rewards'],
        },
      ],
    };
  },
  computed: {
    activeStep() {
      return this.steps[this.activeIndex];
    },
    spokes() {
      // Hairlines from just outside the core to just short of each orb
      const n = this.steps.length;
      return this.steps.map((step, i) => {
        const a = ((-90 + (i * 360) / n) * Math.PI) / 180;
        return {
          x1: +(50 + 15 * Math.cos(a)).toFixed(2),
          y1: +(50 + 15 * Math.sin(a)).toFixed(2),
          x2: +(50 + 33 * Math.cos(a)).toFixed(2),
          y2: +(50 + 33 * Math.sin(a)).toFixed(2),
        };
      });
    },
    particles() {
      // Deterministic scatter: golden-angle spiral, so it renders the same
      // on every visit and never reflows
      const out = [];
      for (let i = 0; i < 26; i++) {
        const a = (i * 137.5 * Math.PI) / 180;
        const r = 14 + ((i * 53) % 66) * 0.5;
        const size = 1.5 + (i % 3) * 0.75;
        out.push({
          hue: i % 5 === 0 ? 'is-pink' : i % 3 === 0 ? 'is-teal' : 'is-white',
          style: {
            left: (50 + r * Math.cos(a)).toFixed(1) + '%',
            top: (50 + r * Math.sin(a)).toFixed(1) + '%',
            width: size + 'px',
            height: size + 'px',
            animationDelay: (i * 0.37).toFixed(2) + 's',
          },
        });
      }
      return out;
    },
  },
  mounted() {
    const reducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!reducedMotion) this.startAuto();
  },
  beforeDestroy() {
    this.stopAuto();
  },
  methods: {
    arcRotate(dashLen) {
      // SVG circle strokes start at 3 o'clock and run clockwise. With
      // pathLength 100, a dash of length L spans L * 3.6 degrees. Rotate so
      // the dash ENDS at the active node (12 o'clock + rotAngle).
      return this.rotAngle - 90 - dashLen * 3.6;
    },
    nodePos(i) {
      // Nodes clockwise from 12 o'clock, on the same r=40 circle the SVG draws
      const angle = ((-90 + (i * 360) / this.steps.length) * Math.PI) / 180;
      return {
        left: `${50 + 40 * Math.cos(angle)}%`,
        top: `${50 + 40 * Math.sin(angle)}%`,
      };
    },
    advanceTo(i, alwaysForward) {
      const n = this.steps.length;
      const stepAngle = 360 / n;
      let delta;
      if (alwaysForward) {
        // Auto-tour: the dot only ever travels clockwise around the loop
        delta = ((i - this.activeIndex) % n + n) % n;
      } else {
        // Manual pick: shortest path around the ring, either direction
        delta = ((i - this.activeIndex + n + Math.floor(n / 2)) % n) - Math.floor(n / 2);
      }
      this.rotAngle += delta * stepAngle;
      this.activeIndex = i;
    },
    select(i, fromHover) {
      if (i !== this.activeIndex) this.advanceTo(i, false);
      // A hover peek pauses the tour; a click ends it for good
      if (!fromHover) {
        this.interacted = true;
        this.stopAuto();
      }
    },
    startAuto() {
      if (this.interacted || this.autoTimer) return;
      this.autoTimer = setInterval(() => {
        this.advanceTo((this.activeIndex + 1) % this.steps.length, true);
      }, 5000);
    },
    stopAuto() {
      if (this.autoTimer) {
        clearInterval(this.autoTimer);
        this.autoTimer = null;
      }
    },
    pauseAuto() {
      this.stopAuto();
    },
    resumeAuto() {
      if (!this.interacted) this.startAuto();
    },
  },
};
</script>

<style lang="scss" scoped>
@import 'src/assets/sass/marketing-tokens';

.journey-section {
  padding: 4rem 0;
}

/* ---------- Desktop: ring + story card ---------- */

.journey-duo {
  display: grid;
  grid-template-columns: minmax(0, 420px) minmax(0, 1fr);
  gap: 3rem;
  align-items: center;
  max-width: 980px;
  margin: 1.5rem auto 0;
}

.journey-ring-wrap {
  position: relative;
  width: 100%;
  aspect-ratio: 1;

  // Space vignette: a darker halo that gives the constellation its depth
  &::before {
    content: '';
    position: absolute;
    inset: -10%;
    border-radius: 50%;
    background: radial-gradient(
      circle,
      rgba(8, 9, 22, 0.6) 0%,
      rgba(8, 9, 22, 0.25) 45%,
      transparent 70%
    );
    pointer-events: none;
  }
}

/* Starfield dots */
.journey-particle {
  position: absolute;
  border-radius: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
  animation: particle-twinkle 4.2s ease-in-out infinite;

  &.is-white { background: rgba(255, 255, 255, 0.5); }
  &.is-teal  { background: rgba(46, 201, 183, 0.7); }
  &.is-pink  { background: rgba(225, 78, 202, 0.65); }
}

@keyframes particle-twinkle {
  0%, 100% { opacity: 0.15; }
  50%      { opacity: 0.7; }
}

@media (prefers-reduced-motion: reduce) {
  .journey-particle { animation: none; opacity: 0.35; }
}

/* Core: the cycle's constant, the one record every step feeds */
.journey-ring-core {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;

  .core-orb {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 96px;
    height: 96px;
    border-radius: 50%;
    background: radial-gradient(circle at 50% 35%, #2b2d55, #15162e 75%);
    border: 1px solid rgba(255, 255, 255, 0.1);
    box-shadow:
      0 0 40px rgba(225, 78, 202, 0.22),
      0 0 90px rgba(46, 201, 183, 0.14);

    img { display: block; }
  }

  .core-label {
    position: absolute;
    top: calc(50% + 62px);
    left: 0;
    right: 0;
    text-align: center;
    color: rgba(255, 255, 255, 0.72);
    font-size: 0.68rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.22em;
    line-height: 1.5;
  }
}

/* The story card: solid surface, real contrast */
.journey-detail-card {
  position: relative;
  background: #232447;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 14px;
  padding: 2rem 2.25rem;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.35);

  .detail-count {
    display: inline-block;
    color: $teal;
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.14em;
    margin-bottom: 0.7rem;
  }

  h3 {
    color: white;
    font-size: 1.6rem;
    font-weight: 700;
    margin: 0 0 0.6rem;
  }

  p {
    color: rgba(255, 255, 255, 0.85);
    font-size: 1rem;
    line-height: 1.6;
    margin: 0 0 1rem;
  }
}

.journey-detail-nav {
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
  display: flex;
  gap: 0.4rem;

  button {
    width: 34px;
    height: 34px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.14);
    color: $muted;
    cursor: pointer;
    transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease;

    &:hover {
      color: white;
      border-color: $primary-border;
      background: $primary-soft;
    }
  }

  .nav-prev { transform: rotate(180deg); }
}

.journey-ring-svg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

/* Comet-tail arcs rotate to follow the active node */
.journey-arc {
  transform-origin: 50% 50%;
  transition: transform 0.7s cubic-bezier(0.45, 0.05, 0.35, 1);
}

/* The orbiting customer record: a rotating frame with a glowing dot at 12 o'clock */
.journey-orbiter {
  position: absolute;
  inset: 0;
  pointer-events: none;
  transition: transform 0.7s cubic-bezier(0.45, 0.05, 0.35, 1);
  z-index: 1;
}

.orbit-dot {
  position: absolute;
  top: 10%;
  left: 50%;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  transform: translate(-50%, -50%);
  background: $primary;
  border: 2px solid rgba(255, 255, 255, 0.85);
  box-shadow:
    0 0 10px rgba(225, 78, 202, 1),
    0 0 28px rgba(225, 78, 202, 0.6);
}

@media (prefers-reduced-motion: reduce) {
  .journey-orbiter,
  .journey-arc { transition: none; }
}

.journey-ring-node {
  position: absolute;
  transform: translate(-50%, -50%);
  width: 64px;
  height: 64px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  z-index: 2;

  // Soft glowing orb, centered exactly on the track point
  .node-orb {
    position: absolute;
    inset: 0;
    border-radius: 50%;
    transition: transform 0.25s ease, filter 0.25s ease;

    &.hue-pink {
      background: radial-gradient(
        circle at 50% 38%,
        rgba(225, 78, 202, 0.5),
        rgba(225, 78, 202, 0.14) 58%,
        rgba(225, 78, 202, 0.03) 76%,
        transparent 80%
      );
      .node-spark {
        background: #ff7ae0;
        box-shadow: 0 0 8px rgba(225, 78, 202, 0.9);
      }
      &::after { border-color: rgba(225, 78, 202, 0.6); }
    }

    &.hue-teal {
      background: radial-gradient(
        circle at 50% 38%,
        rgba(46, 201, 183, 0.5),
        rgba(46, 201, 183, 0.14) 58%,
        rgba(46, 201, 183, 0.03) 76%,
        transparent 80%
      );
      .node-spark {
        background: #4fe3d0;
        box-shadow: 0 0 8px rgba(46, 201, 183, 0.9);
      }
      &::after { border-color: rgba(46, 201, 183, 0.6); }
    }
  }

  .node-spark {
    position: absolute;
    top: 16%;
    left: 50%;
    transform: translateX(-50%);
    width: 9px;
    height: 9px;
    border-radius: 50%;
  }

  // Name + module caption hang below the orb, like the reference
  .node-text {
    position: absolute;
    top: calc(100% - 8px);
    left: 50%;
    transform: translateX(-50%);
    width: 110px;
    text-align: center;
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
    pointer-events: none;
  }

  .node-label {
    color: rgba(255, 255, 255, 0.85);
    font-size: 0.84rem;
    font-weight: 600;
    transition: color 0.2s ease;
  }

  .node-caption {
    color: rgba(255, 255, 255, 0.62);
    font-size: 0.56rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    transition: color 0.2s ease;
  }

  &:hover .node-orb {
    filter: brightness(1.25);
  }

  &.active {
    .node-orb {
      transform: scale(1.14);
      filter: brightness(1.4) saturate(1.1);

      // Radar ping radiating from the active stage
      &::after {
        content: '';
        position: absolute;
        inset: 12%;
        border-radius: 50%;
        border: 1px solid;
        animation: node-ping 1.9s ease-out infinite;
      }
    }
    .node-label { color: white; }
    .node-caption { color: rgba(255, 255, 255, 0.85); }
  }
}

@keyframes node-ping {
  0%   { transform: scale(1);    opacity: 0.7; }
  100% { transform: scale(1.65); opacity: 0; }
}

@media (prefers-reduced-motion: reduce) {
  .journey-ring-node.active .node-orb::after { animation: none; display: none; }
}


.journey-fade-enter-active {
  transition: opacity 0.28s ease, transform 0.28s ease;
}
.journey-fade-leave-active {
  transition: opacity 0.14s ease, transform 0.14s ease;
}
.journey-fade-enter {
  opacity: 0;
  transform: translateY(10px);
}
.journey-fade-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

/* ---------- Shared chips ---------- */

.journey-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;

  span {
    display: inline-block;
    padding: 0.25rem 0.6rem;
    border-radius: 999px;
    border: 1px solid rgba(255, 255, 255, 0.16);
    background: rgba(255, 255, 255, 0.03);
    color: rgba(255, 255, 255, 0.78);
    font-size: 0.72rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    line-height: 1.2;
    white-space: nowrap;
  }
}

/* ---------- Mobile vertical thread ---------- */

.journey-mobile { display: none; }

.journey {
  position: relative;
  margin: 0 auto;

  &::before {
    content: '';
    position: absolute;
    top: 22px;
    bottom: 22px;
    left: 22px;
    width: 2px;
    background: linear-gradient(
      180deg,
      rgba(225, 78, 202, 0),
      rgba(225, 78, 202, 0.5) 8%,
      rgba(225, 78, 202, 0.5) 92%,
      rgba(225, 78, 202, 0)
    );
  }
}

.journey-step {
  display: grid;
  grid-template-columns: 56px 1fr;
  align-items: flex-start;
  margin-bottom: 1.25rem;

  &:last-child { margin-bottom: 0; }
}

.journey-node {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #1d1e3a;
  border: 1px solid $primary-border;
  color: $decorative;
  z-index: 1;
}

.journey-card {
  background: $card-bg;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
  padding: 1.25rem 1.4rem;

  h3 {
    color: white;
    font-size: 1.05rem;
    font-weight: 700;
    margin: 0 0 0.5rem;
  }

  p {
    color: $muted;
    font-size: 0.9rem;
    line-height: 1.55;
    margin: 0 0 0.85rem;
  }
}

/* ---------- Loop line ---------- */

.journey-loop {
  text-align: center;
  color: $muted;
  font-size: 1rem;
  font-weight: 500;
  margin: 2.25rem 0 0;
}

/* ---------- Breakpoint swap ---------- */

@media (max-width: 991px) {
  .journey-duo {
    grid-template-columns: 1fr;
    gap: 2rem;
    max-width: 480px;
  }
}

@media (max-width: 767px) {
  .journey-section { padding: 3rem 0; }
  .journey-duo { display: none; }
  .journey-mobile { display: block; }
}
</style>
